import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { modal } from '@client/components/_ui/feedback/modal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/get-file')({
    component: GetFile,
    validateSearch: zodValidator(
        z.object({
            key: z.string().catch(''),
        }),
    ),
});

function GetFile() {
    const { key } = Route.useSearch();
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { data: url, error } = trpc.file.getDownloadUrl.useQuery(key ? { key: decodeURIComponent(key) } : skipToken);

    useEffect(() => {
        // Show loading modal immediately
        const modalInstance = modal.info({
            title: 'Downloading File',
            content: (
                <div className="flex flex-col items-center justify-center py-8">
                    <Loading size="large" className="scale-150" />
                    <p className="mt-6 text-lg text-muted-foreground">Please wait while we prepare your file...</p>
                </div>
            ),
            centered: true,
            footer: null,
            closable: false,
            mask: { closable: false },
            width: 500,
            className: 'prominent-modal',
        });

        if (url) {
            // Update modal content on success
            modalInstance.update({
                title: 'File Ready',
                content: (
                    <div className="flex flex-col items-center justify-center py-8">
                        <div className="mb-6 text-6xl text-green-500">✓</div>
                        <p className="text-lg text-muted-foreground">Your file will begin downloading shortly...</p>
                        <Button
                            className="mt-6"
                            type="primary"
                            onClick={() => {
                                window.close();
                            }}
                        >
                            Close
                        </Button>
                    </div>
                ),
                mask: { closable: true },
                footer: false,
                width: 500,
                className: 'prominent-modal',
                centered: true,
            });
            window.open(url, '_self');
        }

        if (error) {
            // Update modal content on error
            modalInstance.update({
                title: 'Download Failed',
                content: (
                    <div className="flex flex-col items-center justify-center py-8">
                        <div className="mb-6 text-6xl text-red-500">✕</div>
                        <p className="text-lg text-muted-foreground">Unable to download the file. Please try again.</p>
                        <p className="mt-3 text-sm text-subtle-foreground">{error.message}</p>
                        <div className="mt-6 space-x-4">
                            <Button type="primary" onClick={() => window.location.reload()}>
                                Try Again
                            </Button>
                            <Button
                                type="secondary"
                                onClick={() => {
                                    window.close();
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                ),
                footer: false,
                width: 500,
                className: 'prominent-modal',
                centered: true,
            });
        }

        return () => modalInstance.destroy();
    }, [url, error, navigate]);

    return <PageController route={Route}>{null}</PageController>; // Modal handles all UI states
}
